<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5 class="mb-0">Input Pengeluaran Kas Kecil</h5>
            </template>

            <b-row>
                <b-col cols="12" md="6">
                    <b-form-group label="Barang/Jasa">
                        <StateInputHandler
                            :isLoading="isLoadingItemTypes"
                            :error="itemTypeError"
                            fullWidth
                            errorMessage="Gagal memuat barang/jasa."
                            @retry="getItemTypes"
                        >
                            <multiselect
                                v-model="form.itemType"
                                :options="filteredItemTypeOptions"
                                :internal-search="true"
                                :allow-empty="false"
                                :disabled="isMainInputLocked"
                                deselect-label=""
                                select-label=""
                                label="name"
                                track-by="id"
                                name="kasKecilItemTypeInput"
                                placeholder="Pilih barang/jasa"
                            >
                                <template slot="noResult">Barang atau jasa tidak tersedia</template>
                                <template slot="noOptions">Barang atau jasa tidak tersedia</template>
                            </multiselect>
                        </StateInputHandler>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group label="Status Barang/Jasa">
                        <b-form-select
                            v-model="form.status"
                            :options="expenseStatusOptions"
                            name="kasKecilExpenseStatusInput"
                            :disabled="isMainInputLocked"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group label="Jumlah">
                        <b-form-input
                            v-model.number="form.qty"
                            type="number"
                            min="1"
                            name="kasKecilQtyInput"
                            placeholder="Masukkan jumlah"
                            :disabled="isMainInputLocked"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group label="Harga Per Satuan">
                        <b-input-group prepend="Rp.">
                            <b-form-input
                                v-model="form.price"
                                name="kasKecilPriceInput"
                                placeholder="Masukkan harga"
                                :disabled="isMainInputLocked"
                            />
                        </b-input-group>
                    </b-form-group>
                </b-col>
                <b-col cols="12"></b-col>
                <b-col cols="12" class="mb-3">
                    <b-button
                        variant="pgiBtn"
                        name="kasKecilCheckPriceBtn"
                        :disabled="isCheckingHarga || isMainInputLocked"
                        @click="checkHarga"
                    >
                        <b-spinner v-if="isCheckingHarga" small class="mr-2" />
                        <i v-else class="fa fa-search mr-2"></i>Lanjutkan
                    </b-button>
                    <b-button
                        v-if="isMainInputLocked"
                        variant="secondary"
                        class="ml-2"
                        name="kasKecilResetCheckBtn"
                        @click="resetInputAfterCheck"
                    >
                        Reset
                    </b-button>
                </b-col>

                <template v-if="isHargaChecked">
                    <b-col cols="12">
                        <b-alert :variant="isFraudPotential ? 'warning' : 'success'" show>
                            <strong>{{ hargaCheckMessage }}</strong>
                        </b-alert>
                    </b-col>
                    <b-col v-if="canContinueInput" cols="12" md="6">
                        <b-form-group label="Catatan (Opsional)">
                            <b-form-textarea
                                v-model="form.notes"
                                rows="3"
                                maxlength="255"
                                name="kasKecilNotesInput"
                                placeholder="Masukkan catatan"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col v-if="canContinueInput" cols="12"></b-col>
                    <b-col v-if="canContinueInput" cols="12" md="6" class="kas-kecil-upload-column">
                        <KasKecilUploadPhotoBox
                            class="mb-3"
                            title="Upload Invoice"
                            :photos="invoicePhotos"
                            @add-photo="addInvoicePhoto"
                            @remove-photo="removeInvoicePhoto"
                            @upload-photo="({ photo, file }) => onUploadFile(photo, file, 'invoice')"
                            @preview-photo="photo => openFilePreview(photo, 'Invoice')"
                        />
                        <KasKecilUploadPhotoBox
                            v-if="isBarangDiterima"
                            title="Upload Bukti Diterima"
                            :photos="receiptPhotos"
                            @add-photo="addReceiptPhoto"
                            @remove-photo="removeReceiptPhoto"
                            @upload-photo="({ photo, file }) => onUploadFile(photo, file, 'proof')"
                            @preview-photo="photo => openFilePreview(photo, 'Bukti Diterima')"
                        />
                    </b-col>
                </template>
            </b-row>

            <div class="border-top border-bottom py-3 mb-3" :class="{ 'mt-4': canContinueInput }">
                <div class="d-flex justify-content-between">
                    <span class="text-muted">Total Pengeluaran</span>
                    <strong>{{ formatCurrency(formTotal) }}</strong>
                </div>
            </div>

            <div class="d-flex justify-content-end gap-2">
                <b-button variant="secondary" @click="goBack">
                    Batal
                </b-button>
                <b-button
                    variant="pgiBtn"
                    name="kasKecilSubmitPengeluaranBtn"
                    :disabled="!canContinueInput"
                    @click="openSubmitConfirm"
                >
                    Simpan
                </b-button>
            </div>
        </b-card>

        <ModalKasKecilFilePreview
            id="modal-preview-file-kas-kecil"
            :title="activeFilePreview.title"
            :url="activeFilePreview.url"
            :type="activeFilePreview.type"
        />
        <ModalKasKecilInputPengeluaranConfirm
            :isSubmitting="isSubmittingPengeluaran"
            :total.sync="confirmTotalPengeluaran"
            @submit="submitPengeluaran"
        />
    </div>
</template>

<script>
import CONSTANTS from '@/constants'
import StateInputHandler from '@/components/common/StateInputHandler'
import KasKecilUploadPhotoBox from '@/components/kasKecil/KasKecilUploadPhotoBox'
import ModalKasKecilFilePreview from '@/components/modals/ModalKasKecilFilePreview'
import ModalKasKecilInputPengeluaranConfirm from '@/components/modals/ModalKasKecilInputPengeluaranConfirm'

const STATUS_LOG = CONSTANTS.STATUS_LOG
const MAX_UPLOAD_COUNT = 5

export default {
    name: "input-pengeluaran-kas-kecil",
    components: {
        StateInputHandler,
        KasKecilUploadPhotoBox,
        ModalKasKecilFilePreview,
        ModalKasKecilInputPengeluaranConfirm,
    },
    data() {
        return {
            initStatus: 0,
            detail: {
                id: Number(this.$route.params.id || 0),
                cabang: { id: 2, name: "Cabang Kebon Jeruk" },
                requestedCash: 1000000,
            },
            currentCash: 500000,
            form: {
                itemType: null,
                price: "",
                qty: 1,
                status: STATUS_LOG.OK,
                notes: "",
            },
            isHargaChecked: false,
            hargaCheckResult: null,
            hargaCheckErrorMessage: "",
            isCheckingHarga: false,
            isLoadingItemTypes: false,
            itemTypeError: null,
            isSubmittingPengeluaran: false,
            confirmTotalPengeluaran: "",
            nextPhotoIndex: 3,
            invoicePhotos: [this.createUploadFoto(1)],
            receiptPhotos: [this.createUploadFoto(2)],
            activeFilePreview: {
                title: "Preview Upload",
                type: "image",
                url: "",
            },
            itemTypeOptions: [],
            expenseStatusOptions: [
                { value: STATUS_LOG.OK, text: "Barang/Jasa Diterima" },
                { value: STATUS_LOG.SEDANG_PEMESANAN, text: "Sedang Pemesanan" },
            ],
        }
    },
    computed: {
        filteredItemTypeOptions() {
            return this.itemTypeOptions
        },
        isBarangDiterima() {
            return this.form.status === STATUS_LOG.OK
        },
        formTotal() {
            return this.getFormPriceValue() * Number(this.form.qty || 0)
        },
        confirmTotalPengeluaranValue() {
            return Number(this.$helper.removeThousandSeparator(this.confirmTotalPengeluaran || 0))
        },
        selectedItemPriceLimit() {
            if (!this.form.itemType) return 0
            return Number(this.form.itemType.maxPricePerQty || this.form.itemType.priceLimit || 0)
        },
        isFraudPotential() {
            if (this.canBypassHargaCheck) return false
            if (this.hargaCheckResult) return Number(this.hargaCheckResult.id) !== 2
            if (this.form.itemType && !this.$helper.isNullOrEmpty(this.form.itemType.fraudPotential)) {
                return this.form.itemType.fraudPotential === true
            }
            if (!this.selectedItemPriceLimit) return false
            return this.getFormPriceValue() > this.selectedItemPriceLimit
        },
        canContinueInput() {
            return this.isHargaChecked && !this.isFraudPotential
        },
        isMainInputLocked() {
            return this.canContinueInput
        },
        canBypassHargaCheck() {
            return this.$helper.hasPermissionMenuChildren("MBH")
        },
        isHargaCheckBypassed() {
            return this.isHargaChecked && this.canBypassHargaCheck
        },
        hargaCheckMessage() {
            if (this.isFraudPotential) {
                return this.hargaCheckErrorMessage
                    || "Harga melebihi batas yang ditentukan. Silakan lakukan pengajuan melalui Program Online (PO)."
            }
            if (this.isHargaCheckBypassed) return "Validasi harga dilewati oleh otorisasi khusus. Pengeluaran dapat dilanjutkan."
            return "Pengeluaran dapat dilanjutkan."
        },
    },
    methods: {
        init() {
            this.initStatus = 0
            this.loadCabangProvince()
                .then(() => this.getItemTypes())
                .then(() => {
                    this.initStatus = 1
                })
                .catch((error) => {
                    this.$helper.parseError(this, error)
                    this.initStatus = -1
                })
        },
        loadCabangProvince() {
            const cabangId = this.$helper.getCabangId()
            return this.$axios.get(`/cabang/${cabangId}`).then((response) => {
                localStorage.setItem("activeCabangProvinceId", response.data.provinsi.id)
            })
        },
        getItemTypes() {
            this.isLoadingItemTypes = true
            this.itemTypeError = null
            return this.$axios.get("mini-cash/item-type/", {
                params: {
                    provinceId: this.$helper.getCabangProvinceId(),
                },
            }).then((response) => {
                const data = Array.isArray(response.data) ? response.data : []
                const sortedData = data.sort((a, b) => {
                    const catA = (a.categoryName || "").toLowerCase()
                    const catB = (b.categoryName || "").toLowerCase()
                    if (catA !== catB) return catA.localeCompare(catB)
                    return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
                })
                this.itemTypeOptions = sortedData.map((item) => ({
                    ...item,
                    name: this.getItemTypeLabel(item),
                }))
            }).catch((error) => {
                this.itemTypeOptions = []
                this.itemTypeError = this.$helper.parseError(this, error, "Gagal", false)
            }).finally(() => {
                this.isLoadingItemTypes = false
            })
        },
        getItemTypeLabel(item) {
            const category = item.categoryName || "-"
            const name = item.name || "-"
            if (this.$helper.isNullOrEmpty(item.unit)) return `${category} - ${name}`
            return `${category} - ${name} (Per ${item.unit})`
        },
        createUploadFoto(index) {
            return {
                index,
                file: null,
                fileName: "",
                previewUrl: "",
                publicUrl: "",
                isUploading: false,
            }
        },
        checkHarga() {
            if (!this.validateMainInput()) return

            this.isCheckingHarga = true
            this.hargaCheckErrorMessage = ""
            this.$axios.post("mini-cash/input-log/", {}, {
                params: this.buildInputLogParams(0),
            }).then((response) => {
                this.hargaCheckResult = response.data || {}
                this.isHargaChecked = true
            }).catch((error) => {
                this.hargaCheckResult = { id: 0 }
                this.hargaCheckErrorMessage = this.$helper.parseError(this, error, "Gagal", false)
                this.isHargaChecked = true
            }).finally(() => {
                this.isCheckingHarga = false
            })
        },
        buildInputLogParams(finalize = 0) {
            const params = {
                miniCashCabangId: this.detail.id,
                miniCashItemTypeId: this.form.itemType.id,
                pricePerQty: this.getFormPriceValue(),
                qty: Number(this.form.qty || 0),
                status: this.form.status,
                finalize,
            }

            if (this.canBypassHargaCheck) {
                params.bypassPrice = "MBH"
            }

            if (finalize === 1) {
                params.photoInvoice = this.getUploadedUrls(this.invoicePhotos)
                params.notes = this.form.notes || ""
                if (this.isBarangDiterima) {
                    params.photoProof = this.getUploadedUrls(this.receiptPhotos)
                }
            }

            return params
        },
        openSubmitConfirm() {
            if (!this.validateMainInput()) return

            if (!this.canContinueInput) {
                this.$helper.toastErr(this, "Harga melebihi limit. Silakan hubungi PO.")
                return
            }

            if (!this.hasUploadedFile(this.invoicePhotos)) {
                this.$helper.toastErr(this, "Invoice wajib diupload.")
                return
            }

            if (this.hasIncompleteUpload(this.invoicePhotos)) {
                this.$helper.toastErr(this, "Upload invoice wajib dilengkapi atau dihapus.")
                return
            }

            if (this.isBarangDiterima && !this.hasUploadedFile(this.receiptPhotos)) {
                this.$helper.toastErr(this, "Bukti diterima wajib diupload.")
                return
            }

            if (this.isBarangDiterima && this.hasIncompleteUpload(this.receiptPhotos)) {
                this.$helper.toastErr(this, "Upload bukti diterima wajib dilengkapi atau dihapus.")
                return
            }

            this.confirmTotalPengeluaran = ""
            this.$bvModal.show("modal-confirm-input-pengeluaran-kas-kecil")
        },
        submitPengeluaran() {
            if (!this.validateConfirmTotalPengeluaran()) return

            this.isSubmittingPengeluaran = true
            this.$axios.post(this.buildInputLogUrl(1))
                .then(() => {
                    this.$bvModal.hide("modal-confirm-input-pengeluaran-kas-kecil")
                    this.goBack()
                }).catch((error) => {
                    this.$helper.parseError(this, error)
                }).finally(() => {
                    this.isSubmittingPengeluaran = false
                })
        },
        buildInputLogUrl(finalize = 0) {
            return `mini-cash/input-log/?${this.buildQueryString(this.buildInputLogParams(finalize))}`
        },
        buildQueryString(params) {
            return Object.keys(params)
                .map((key) => {
                    const value = params[key]
                    const normalizedValue = key === "photoInvoice" || key === "photoProof"
                        ? value
                        : encodeURIComponent(value)
                    return `${encodeURIComponent(key)}=${normalizedValue}`
                })
                .join("&")
        },
        validateMainInput() {
            if (this.$helper.isNullOrEmpty(this.form.itemType)) {
                this.$helper.toastErr(this, "Barang/Jasa wajib diisi.")
                return false
            }

            const price = this.getFormPriceValue()

            if (price <= 0) {
                this.$helper.toastErr(this, "Harga wajib lebih dari 0.")
                return false
            }

            if (Number(this.form.qty || 0) <= 0) {
                this.$helper.toastErr(this, "Jumlah wajib lebih dari 0.")
                return false
            }

            if (this.$helper.isNullOrEmpty(this.form.status)) {
                this.$helper.toastErr(this, "Status Barang/Jasa wajib diisi.")
                return false
            }

            return true
        },
        getFormPriceValue() {
            return Number(this.$helper.removeThousandSeparator(this.form.price || 0))
        },
        formatCurrency(value) {
            return `Rp. ${this.$helper.addThousandSeparator(Number(value || 0))}`
        },
        formatCurrencyInput(value) {
            if (this.$helper.isNullOrEmpty(value)) return ""
            const cleanValue = this.$helper.changeToValue(String(value))
            return cleanValue.replace(/(?!^-)[^0-9]/g, "")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        },
        validateConfirmTotalPengeluaran() {
            if (this.confirmTotalPengeluaranValue <= 0) {
                this.$helper.toastErr(this, "Total Pengeluaran wajib lebih dari 0.")
                return false
            }

            if (this.confirmTotalPengeluaranValue !== this.formTotal) {
                this.$helper.toastErr(this, "Total Pengeluaran tidak sesuai.")
                return false
            }

            return true
        },
        goBack() {
            this.$router.push({ path: `/kasir/kas-kecil/${this.detail.id}` })
        },
        addInvoicePhoto() {
            if (!this.canAddUpload(this.invoicePhotos)) return
            this.invoicePhotos.push(this.createUploadFoto(this.nextPhotoIndex))
            this.nextPhotoIndex += 1
        },
        addReceiptPhoto() {
            if (!this.canAddUpload(this.receiptPhotos)) return
            this.receiptPhotos.push(this.createUploadFoto(this.nextPhotoIndex))
            this.nextPhotoIndex += 1
        },
        canAddUpload(files) {
            if (files.length < MAX_UPLOAD_COUNT) return true

            this.$helper.toastErr(this, `Maksimal upload ${MAX_UPLOAD_COUNT} file.`)
            return false
        },
        removeInvoicePhoto(index) {
            if (this.invoicePhotos.length <= 1) {
                this.revokeUploadPreview(this.invoicePhotos[0])
                this.invoicePhotos = [this.createUploadFoto(1)]
                return
            }
            this.invoicePhotos
                .filter((item) => item.index === index)
                .forEach((item) => this.revokeUploadPreview(item))
            this.invoicePhotos = this.invoicePhotos.filter((item) => item.index !== index)
        },
        removeReceiptPhoto(index) {
            if (this.receiptPhotos.length <= 1) {
                this.revokeUploadPreview(this.receiptPhotos[0])
                this.receiptPhotos = [this.createUploadFoto(2)]
                return
            }
            this.receiptPhotos
                .filter((item) => item.index === index)
                .forEach((item) => this.revokeUploadPreview(item))
            this.receiptPhotos = this.receiptPhotos.filter((item) => item.index !== index)
        },
        hasUploadedFile(files) {
            return files.some((item) => !this.$helper.isNullOrEmpty(item.publicUrl))
        },
        hasIncompleteUpload(files) {
            return files.some((item) => this.$helper.isNullOrEmpty(item.publicUrl))
        },
        getUploadedUrls(files) {
            return files
                .map((item) => this.getUploadPath(item.publicUrl))
                .filter((url) => !this.$helper.isNullOrEmpty(url))
                .join(",")
        },
        getUploadPath(url) {
            if (this.$helper.isNullOrEmpty(url)) return ""

            return String(url).replace(/^https?:\/\/[^/]+\//, "")
        },
        async onUploadFile(item, selectedFile = null, type = "invoice") {
            const file = selectedFile || item.file
            if (!file) return

            if (!this.isValidUploadFile(file)) {
                this.resetUploadFile(item)
                this.$helper.toastErr(this, "File harus berupa foto atau PDF.")
                return
            }

            item.isUploading = true
            this.revokeUploadPreview(item)
            item.publicUrl = ""
            item.fileName = ""
            this.$store.dispatch("loading/show", { progress: 0 })

            let response
            try {
                response = await this.$helper.uploadPresigned({
                    vue: this,
                    path: this.getPresignedUploadPath(type),
                    params: { fileExtension: this.getFileExtension(file) },
                    fileUpload: file,
                    contentType: this.getUploadContentType(file),
                    onUploadProgress: progress => this.setUploadProgress(progress),
                    useXhr: true,
                })
            } catch (error) {
                this.$helper.parseError(this, error)
            } finally {
                item.isUploading = false
                this.$store.dispatch("loading/hide")
            }

            if (!response || !response.success) {
                this.resetUploadFile(item)
                this.$helper.toastErr(this, "Gagal upload file. Silakan coba lagi.")
                return
            }

            item.previewUrl = URL.createObjectURL(file)
            item.publicUrl = response.publicUrl
            item.fileName = file.name
        },
        getPresignedUploadPath(type) {
            return type === "proof"
                ? "mini-cash/create-presigned-url-proof"
                : "mini-cash/create-presigned-url-invoice"
        },
        setUploadProgress(progress) {
            this.$store.dispatch("loading/setProgress", Math.min(progress, 100))
        },
        resetUploadFile(item) {
            this.revokeUploadPreview(item)
            item.file = null
            item.fileName = ""
            item.publicUrl = ""
        },
        revokeUploadPreview(item) {
            if (!item.previewUrl) return

            URL.revokeObjectURL(item.previewUrl)
            item.previewUrl = ""
        },
        openFilePreview(item, label) {
            if (!item.previewUrl) return

            this.activeFilePreview = {
                title: `Preview ${label}`,
                type: this.getPreviewType(item.file),
                url: item.previewUrl,
            }
            this.$bvModal.show("modal-preview-file-kas-kecil")
        },
        getPreviewType(file) {
            if (!file) return "image"
            const type = file.type || ""
            const name = (file.name || "").toLowerCase()

            return type === "application/pdf" || name.endsWith(".pdf") ? "pdf" : "image"
        },
        getUploadContentType(file) {
            return (file && file.type) || "image"
        },
        getFileExtension(file) {
            const name = (file && file.name) || ""
            const extension = name.split(".").pop()
            if (extension && extension !== name) return extension.toLowerCase()

            const type = (file && file.type) || ""
            return type.split("/").pop() || ""
        },
        isValidUploadFile(file) {
            const type = (file && file.type) || ""
            const name = ((file && file.name) || "").toLowerCase()

            return type.startsWith("image/") || type === "application/pdf" || name.endsWith(".pdf")
        },
        resetHargaCheck() {
            this.isHargaChecked = false
            this.hargaCheckResult = null
            this.hargaCheckErrorMessage = ""
        },
        resetInputAfterCheck() {
            this.resetHargaCheck()
            this.form.notes = ""
            this.invoicePhotos.forEach((item) => this.revokeUploadPreview(item))
            this.receiptPhotos.forEach((item) => this.revokeUploadPreview(item))
            this.invoicePhotos = [this.createUploadFoto(1)]
            this.receiptPhotos = [this.createUploadFoto(2)]
            this.nextPhotoIndex = 3
            this.activeFilePreview = {
                title: "Preview Upload",
                type: "image",
                url: "",
            }
        },
    },
    watch: {
        "form.itemType": function () {
            this.resetHargaCheck()
        },
        "form.price": function (newValue) {
            this.resetHargaCheck()
            const result = this.formatCurrencyInput(newValue)
            if (newValue === result) return
            this.$nextTick(() => this.form.price = result)
        },
        confirmTotalPengeluaran: function (newValue) {
            const result = this.formatCurrencyInput(newValue)
            if (newValue === result) return
            this.$nextTick(() => this.confirmTotalPengeluaran = result)
        },
        "form.qty": function () {
            this.resetHargaCheck()
        },
        "form.status": function () {
            this.resetHargaCheck()
            if (!this.isBarangDiterima) {
                this.receiptPhotos.forEach((item) => this.revokeUploadPreview(item))
                this.receiptPhotos = [this.createUploadFoto(2)]
            }
        },
    },
    created() {
        this.init()
    },
    beforeDestroy() {
        [...this.invoicePhotos, ...this.receiptPhotos].forEach((item) => this.revokeUploadPreview(item))
    },
}
</script>
